import {
  emptyPresenterPost,
  NewsletterPreviewResponse,
  NewsletterResponse,
  PostsResponse,
} from '@RestApi/client'
import { dataPost, presenterNewsletter, Post } from '@RestApi/types'
import {
  atom,
  atomFamily,
  selector,
  useRecoilCallback,
  useRecoilState,
  useSetRecoilState,
  waitForAll,
} from 'recoil'

export type RecipientItem = {
  itemId: string
  count: number
  source: string
  type: string
  display: boolean
  acceptedValues?: Array<string>
}

export const componentAutofocusState = atom<boolean>({
  key: 'newsletterComposer.componentAutofocusState',
  default: false,
})
export const newsletterResponseAtom = atom<NewsletterResponse>({
  key: 'newsletterComposer.newsletterResponse',
  default: { status: 420, vctraceid: '' },
})
export const newsletterSubjectAtom = atom<string>({
  key: 'newsletterComposer.subject',
  default: '',
})
export const newsletterRecipientsAtom = atom<Array<RecipientItem>>({
  key: 'newsletterComposer.recipients',
  default: [],
})

export const populateNewsletterPayloadSelector = selector<presenterNewsletter>({
  key: 'newsletterComposer.populateNewsletterPayload',
  get: ({ get }) => {
    const newsletterResponse = get(newsletterResponseAtom)
    const newsletterRecipients = get(newsletterRecipientsAtom)
    const newsletter = newsletterResponse?.data
    if (!newsletter) {
      return {
        id: '',
        name: '',
        hub_id: '',
        post_id: '',
        frequency: '',
        type: '',
        template_id: '',
        from_name: '',
        reply_to: '',
        subject: '',
        roles: [],
        mailing_list_ids: '',
        recipient_count: 0,
        status: '',
        status_reason: '',
      }
    }
    const subject = get(newsletterSubjectAtom)
    const payload = {
      ...newsletter,
      subject: subject,
      roles: newsletterRecipients
        .filter((item) => item.type === 'HubMembers')
        .map((item) => item.source),
      sparq3_filter_ids: newsletterRecipients
        .filter((item) => item.type === 'Filter')
        .map((item) => item.source),
      sparq3_dynamic_filters: newsletterRecipients
        .filter((item) => item.type === 'survey' || item.type === 'active_members')
        .map((item) => ({
          id: item.source,
          type: item.type,
          values: item.acceptedValues,
        })),
      post: undefined,
      user: undefined,
    }
    return payload
  },
})

// Root post of the Newsletter composer, stores header and footer metadata. All individual blocks are its children.
export const composerRootPost = atom<Post>({
  key: 'newsletterComposer.rootPost',
  default: emptyPresenterPost('newsletterRoot'),
})

// Individual blocks of the Newsletter composer. We store reference to each block
// in array of Ids, and then we store the values in the atomFamily(id), so each
// individual post change only re-renders a single block only.
export const composerBlockIds = atom<number[]>({ key: 'newsletterComposer.blockIds', default: [] })
export const composerBlocks = atomFamily<dataPost, number>({
  key: 'newsletterComposer.blocks',
  default: emptyPresenterPost('unknown'),
})

export const populateNewsletterPostPayloadSelector = selector<Post>({
  key: 'newsletterComposer.populateNewsletterPostPayload',
  get: ({ get }) => {
    const rootPost = get(composerRootPost)
    const blockIds = get(composerBlockIds)
    const postPayload = {
      ...rootPost,
      children: get(waitForAll(blockIds.map((blockId) => composerBlocks(blockId)))),
    }
    return postPayload
  },
})

export const newsletterPreviewIsOpenAtom = atom<boolean>({
  key: 'newsletterComposer.newsletterPreviewIsOpen',
  default: false,
})
export const newsletterPreviewIsLoadingAtom = atom<boolean>({
  key: 'newsletterComposer.newsletterPreviewIsLoading',
  default: false,
})
export const newsletterPreviewResponseAtom = atom<NewsletterPreviewResponse>({
  key: 'newsletterComposer.previewResponse',
  default: { status: 420, vctraceid: '', data: { html: '' } },
})

export const postsInSelectModal = atom<PostsResponse>({
  key: 'newsletterComposer.postsInSelectModal',
  default: { status: 420, vctraceid: '', data: [] },
})

// Helper hooks for updating blocks.
let currentBlockId = 1

export const useInitPostBlock = () => {
  const setBlockIds = useSetRecoilState(composerBlockIds)

  return useRecoilCallback(({ set }) => {
    return (blocks: dataPost[]) => {
      blocks.map((block) => {
        const newId = currentBlockId++
        console.log(`init block ${newId}`)

        setBlockIds((blockIds: number[]) => [...blockIds, newId])
        set(composerBlocks(newId), block)
      })
    }
  }, [])
}

export const useInsertPostBlock = () => {
  const [blockIds, setBlockIds] = useRecoilState<number[]>(composerBlockIds)

  return useRecoilCallback(
    ({ set }) => {
      return (block: dataPost, id: number) => {
        const newId = currentBlockId++
        const currentPosition = blockIds.findIndex((item) => item === id)
        setBlockIds((blockIds) => [
          ...blockIds.slice(0, currentPosition + 1),
          newId,
          ...blockIds.slice(currentPosition + 1),
        ])
        set(composerBlocks(newId), block)
      }
    },
    [blockIds]
  )
}

export const useRemoveBlock = () => {
  const [blockIds, setBlockIds] = useRecoilState<number[]>(composerBlockIds)

  return useRecoilCallback(
    ({ reset }) => {
      return (id: number) => {
        setBlockIds((blockIds) => {
          return blockIds.filter((item) => item !== id)
        })
        reset(composerBlocks(id))
      }
    },
    [blockIds]
  )
}

export const useSwapUpPost = () => {
  const [blockIds, setBlockIds] = useRecoilState<number[]>(composerBlockIds)

  return useRecoilCallback(
    ({ reset }) => {
      return (id: number) => {
        const tempBlock = blockIds.find((element) => element === id) as number
        const index = blockIds.findIndex((element) => element === id)
        if (index > 0) {
          setBlockIds((blockIds) => blockIds.filter((item) => item !== id))
          setBlockIds((blockIds) => [
            ...blockIds.slice(0, index - 1),
            tempBlock,
            ...blockIds.slice(index - 1),
          ])
        }
      }
    },
    [blockIds]
  )
}

export const useSwapDownPost = () => {
  const [blockIds, setBlockIds] = useRecoilState<number[]>(composerBlockIds)

  return useRecoilCallback(
    ({ reset }) => {
      return (id: number) => {
        const tempBlock = blockIds.find((element) => element === id) as number
        const index = blockIds.findIndex((element) => element === id)
        if (index <= blockIds.length) {
          setBlockIds((blockIds) => blockIds.filter((item) => item !== id))
          setBlockIds((blockIds) => [
            ...blockIds.slice(0, index + 1),
            tempBlock,
            ...blockIds.slice(index + 1),
          ])
        }
      }
    },
    [blockIds]
  )
}
